import { ReactFlowInstance } from 'reactflow';

import { useDebugModeStore } from '../../Design/state/debugModeStore';
import { convertCanvasToReactFlow } from '../utils';

import { CanvasEdge, CanvasNode, useCanvasStore } from '~/scenes/Build/scenes/FlowBuilder/stores';
import { filterByLevel } from '~/scenes/Build/scenes/FlowBuilder/stores/helpers';

let reactFlowInstance: ReactFlowInstance | null = null;

const pendingUpdate = {
  nodes: null as ReturnType<typeof useCanvasStore.getState>['nodes'] | null,
  edges: null as ReturnType<typeof useCanvasStore.getState>['edges'] | null,
  options: null as { fitView?: boolean } | null
};

export const globalCanvasData = {
  cursorPosition: { x: 0, y: 0 }
};

// Reconcile algorithm to prevent updates on unchanged elements
function reconcile<T extends { id: string }>(existing: T[], next: T[]): T[] {
  const existingMap = new Map(existing.map(item => [item.id, item]));
  return next.map(item => {
    const existingItem = existingMap.get(item.id);
    if (!existingItem) return item;
    const existingPos = (
      existingItem as { position?: { x: number; y: number } }
    ).position;
    const nextPos = (item as { position?: { x: number; y: number } }).position;
    const posChanged =
      existingPos &&
      nextPos &&
      (existingPos.x !== nextPos.x || existingPos.y !== nextPos.y);
    const existingSelected = (existingItem as { selected?: boolean }).selected;
    const nextSelected = (item as { selected?: boolean }).selected;
    const selectedChanged = existingSelected !== nextSelected;
    return posChanged || selectedChanged ? item : existingItem;
  });
}

export function updateReactFlow(
  nodes: ReturnType<typeof useCanvasStore.getState>['nodes'],
  edges: ReturnType<typeof useCanvasStore.getState>['edges'],
  options: { fitView?: boolean } = { fitView: false }
): void {
  if (reactFlowInstance) {
    const converted = convertCanvasToReactFlow(nodes, edges);
    reactFlowInstance.setNodes(existing =>
      reconcile(existing, converted.nodes)
    );
    reactFlowInstance.setEdges(existing =>
      reconcile(existing, converted.edges)
    );

    if (options.fitView) {
      setTimeout(() => {
        reactFlowInstance?.fitView();
      }, 75);
    }
  } else {
    pendingUpdate.nodes = nodes;
    pendingUpdate.edges = edges;
    pendingUpdate.options = options;
  }
}

/**
 * Set the ReactFlow instance and the controlled setters for nodes/edges.
 * Called by FlowCanvas during initialization (onInit callback).
 */
export function setReactFlowInstance(instance: ReactFlowInstance): void {
  reactFlowInstance = instance;

  if (pendingUpdate.nodes && pendingUpdate.edges) {
    updateReactFlow(
      pendingUpdate.nodes,
      pendingUpdate.edges,
      pendingUpdate.options ?? undefined
    );
    pendingUpdate.nodes = null;
    pendingUpdate.edges = null;
    pendingUpdate.options = null;
  }
}

/**
 * Get the ReactFlow instance
 * Returns null if FlowCanvas hasn't initialized yet
 *
 * @returns The ReactFlow instance or null
 */
export function getReactFlowInstance(): ReactFlowInstance | null {
  return reactFlowInstance;
}

export function updateReactFlowWithStores(options?: {
  fitView?: boolean;
}): void {
  const canvasNodes = useCanvasStore.getState().nodes;
  const canvasEdges = useCanvasStore.getState().edges;
  const { isDebugMode } = useDebugModeStore.getState();
  const currentLevelName = isDebugMode
    ? useDebugModeStore.getState().debuggerCurrentLevelName
    : useCanvasStore.getState().currentLevelName;

  const filtered = filterByLevel(canvasNodes, canvasEdges, currentLevelName);
  updateReactFlow(filtered.nodes, filtered.edges, options);
}

export function clearReactFlowInstance(): void {
  reactFlowInstance = null;
  pendingUpdate.nodes = null;
  pendingUpdate.edges = null;
  pendingUpdate.options = null;
}

export function addReactFlowElements(
  nodes: Map<string, CanvasNode> = new Map(),
  edges: Map<string, CanvasEdge> = new Map()
): void {
  if (reactFlowInstance) {
    const converted = convertCanvasToReactFlow(nodes, edges);

    reactFlowInstance.addNodes(converted.nodes);
    reactFlowInstance.addEdges(converted.edges);
  }
}
